import React, { useCallback, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, TextField, Typography
} from "@mui/material";
import {
  CartesianGrid, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import { fetchDpdDayActivity } from "../../../services/dashboardModule/dashboardServices.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { formatDateTime } from "../../../common/constants/dashboardModule/activityConstants.js";

// ── THE NAMING, WRITTEN DOWN ───────────────────────────────────────
//
//   Response %    Responses / Text Sent    did the message land
//   PTP           Kept + Promises          everything committed to
//   Kept %        Kept / PTP               of that, how much arrived
//
// ⚠ THE BACKEND COLUMNS DO NOT USE THESE WORDS. `Payments`/`PaymentAmount` is
// Kept; `PTP`/`PromiseAmount` is PROMISES — the scheduled half only, not PTP.
//
// That mattered the moment PTP moved to sit directly before Kept. In that
// position the two read as whole-then-part, so a promise-only "PTP" beside
// Kept would say "$12K promised, $8K of it arrived" about two figures that do
// not overlap at all — the real total being $20K. So:
//
//   • the PTP tile is now Kept + Promises, summed here
//   • the promise-only figures are no longer displayed at all — not in the
//     matrix cells, not as detail columns. They feed PTP and nothing else.
//
// ⚠ UNCOMMITTED — customers given the payment method who never selected one —
// is a different thing again. It commits to nothing, so it adds to nothing
// and appears nowhere on this page.
//
// The word PTP now means one thing on this page. The COLUMN NAMES are
// untouched; only what the reader sees changed.
//
// ── THE PALETTE IS THE REPORT'S ──
// rpt1's PDF is the artifact people forward: Responses blue, PTP green, Kept a
// darker shade of that green.
const shade = (hex, pct) => {
  const n = parseInt(String(hex).slice(1), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + c * pct)));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map(f).map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};
const KEPT_SHADE = -0.42;

const C_SENT        = "#0C447C";  // navy
const C_RPC         = "#378ADD";  // blue
const C_PTP         = "#1D9E75";  // green
const C_KEPT        = shade(C_PTP, KEPT_SHADE);   // dark green

// Chrome and the non-metric accents. BLUE drives buttons, pills and the
// heatmap totals; AMBER carries response TIME, which is not in the funnel.
const BLUE   = "#378ADD";
const AMBER  = "#BA7517";

// Same 14-slot vocabulary as DPDDashboard / udp_refresh_dpd_day_activity.
const RESP_TIME_BUCKETS = [
  "<1m", "1-5m", "5-15m", "15-30m", "30-60m",
  "1-4h", "4-8h", "8-12h", "12-18h", "18-24h",
  "24-36h", "36-48h", "48-72h", "72h+",
];

// Business-hours heatmap window: 8am–10pm local (matches DPDDashboard).
const HEATMAP_HOURS       = ["8a","9a","10a","11a","12p","1p","2p","3p","4p","5p","6p","7p","8p","9p","10p"];
const HEATMAP_HOUR_VALUES = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22];
const HEATMAP_DAYS        = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

// ── Helpers ────────────────────────────────────────────────────────
function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

function fmtCurrency(n) {
  const num = Number(n);
  if (n === null || n === undefined || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000)    return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

function fmtDate(val) {
  if (!val) return "";
  const s = String(val).slice(0, 10);
  const d = new Date(s.replace(/-/g, "/")); // local-time parse, not UTC
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

// Humanize average first-response latency (minutes).
function fmtDuration(mins) {
  const m = Number(mins);
  if (!isFinite(m) || m <= 0) return "—";
  if (m < 60)   return `${m.toFixed(0)}m`;
  if (m < 1440) return `${(m / 60).toFixed(1)}h`;
  return `${(m / 1440).toFixed(1)}d`;
}

// Shift (UTC day-of-week, UTC hour) → local. day_of_week: 1=Mon … 7=Sun.
function utcToLocal(dayOfWeek, hourOfDay) {
  const offsetMins = new Date().getTimezoneOffset();
  let totalMins = hourOfDay * 60 - offsetMins;
  let dayShift  = 0;
  while (totalMins < 0)     { totalMins += 1440; dayShift -= 1; }
  while (totalMins >= 1440) { totalMins -= 1440; dayShift += 1; }
  const newHour = Math.floor(totalMins / 60);
  const newDay  = ((dayOfWeek - 1 + dayShift) % 7 + 7) % 7 + 1;
  return { day: newDay, hour: newHour };
}

const totalCellSx = {
  height: 22, borderRadius: "3px",
  background: `${BLUE}18`,
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 10, fontWeight: 600, color: BLUE,
};
const grandTotalCellSx = {
  ...totalCellSx,
  background: `${BLUE}33`,
  fontWeight: 700,
};

// ── Sub-components ─────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function KpiTile({ label, value, sub, color = BLUE }) {
  return (
    <Box sx={{
      background: "#f7f7f7", borderRadius: 2, p: "10px 12px",
      flex: 1, minWidth: 0,
      display: "flex", flexDirection: "column", gap: "6px",
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography sx={{
        fontSize: 12,
        color: sub ? "text.primary" : "text.disabled",
        lineHeight: 1.2,
      }}>
        {sub || "—"}
      </Typography>
    </Box>
  );
}

function ChartCard({ title, children, height = 280 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── Response time × DPD (individual day) matrix ────────────────────
// Same layout as DPDDashboard's DpdTimingHeatmap, but columns are the
// distinct DPD days present in the result rather than the 8 buckets.
//
// Cell: count + % of column, KEPT $ (share of column's payment $).
//
// The promise amount had a line here too. It is gone — see the header: the
// promise half feeds PTP and is not displayed on its own.
function DpdDayTimingHeatmap({ rows, dpdValues }) {
  const grid = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      if (!r.RespBucket) return; // NULL = never responded — not on this matrix
      const key = `${r.DPD}_${r.RespBucket}`;
      if (!map[key]) map[key] = { count: 0, keptAmt: 0, uncAmt: 0 };
      map[key].count   += safeNum(r.Responses);
      map[key].keptAmt += safeNum(r.PaymentAmount);
      map[key].uncAmt  += safeNum(r.PromiseAmount);
    });
    return map;
  }, [rows]);

  const cell = (dpd, rt) =>
    grid[`${dpd}_${rt}`] || { count: 0, keptAmt: 0, uncAmt: 0 };

  const max = Math.max(1, ...Object.values(grid).map(v => v.count));

  const rowTotals = RESP_TIME_BUCKETS.map(rt =>
    dpdValues.reduce((s, dpd) => s + cell(dpd, rt).count, 0));
  const colTotals = dpdValues.map(dpd =>
    RESP_TIME_BUCKETS.reduce((s, rt) => s + cell(dpd, rt).count, 0));
  const grandTotal = colTotals.reduce((a, b) => a + b, 0);

  const colKept = dpdValues.map(dpd =>
    RESP_TIME_BUCKETS.reduce((s, rt) => s + cell(dpd, rt).keptAmt, 0));
  const colUnc = dpdValues.map(dpd =>
    RESP_TIME_BUCKETS.reduce((s, rt) => s + cell(dpd, rt).uncAmt, 0));
  const rowKept = RESP_TIME_BUCKETS.map(rt =>
    dpdValues.reduce((s, dpd) => s + cell(dpd, rt).keptAmt, 0));
  const rowUnc = RESP_TIME_BUCKETS.map(rt =>
    dpdValues.reduce((s, dpd) => s + cell(dpd, rt).uncAmt, 0));
  const grandKept = colKept.reduce((a, b) => a + b, 0);
  const grandUnc  = colUnc.reduce((a, b) => a + b, 0);

  const heatColor = (val) => {
    if (!val) return "#F5F5F5";
    const r = val / max;
    if (r < 0.15) return "#E6F1FB";
    if (r < 0.35) return "#B5D4F4";
    if (r < 0.6)  return "#378ADD";
    return "#0C447C";
  };

  // Columns get a floor width so wide DPD ranges scroll instead of crush.
  const colTpl = `80px repeat(${dpdValues.length}, minmax(74px, 1fr)) 86px`;
  const cellH  = 58;
  const totH   = 44;

  return (
    <Box sx={{ overflowX: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "grid", gridTemplateColumns: colTpl, gap: "2px", fontSize: 10, mb: 0.5 }}>
        <Box />
        {dpdValues.map(d => (
          <Box key={d} sx={{ textAlign: "center", color: "text.secondary",
            fontWeight: 500, pb: 0.5 }}>DPD {d}</Box>
        ))}
        <Box sx={{ textAlign: "center", color: BLUE, fontWeight: 600, pb: 0.5 }}>Total</Box>
      </Box>

      {/* Body rows */}
      {RESP_TIME_BUCKETS.map((rt, ri) => (
        <Box key={rt} sx={{ display: "grid", gridTemplateColumns: colTpl, gap: "2px", mb: "2px" }}>
          <Box sx={{ fontSize: 10, color: "text.secondary",
            lineHeight: `${cellH}px`, textAlign: "right", pr: 1 }}>
            {rt}
          </Box>
          {dpdValues.map((dpd, di) => {
            const c        = cell(dpd, rt);
            const countPct = colTotals[di] > 0 ? (c.count / colTotals[di]) * 100 : 0;
            const keptPct  = colKept[di] > 0 ? (c.keptAmt / colKept[di]) * 100 : 0;
            const isLight   = c.count <= max * 0.55;
            const textColor = isLight ? "text.primary"   : "#fff";
            const subColor  = isLight ? "text.secondary" : "rgba(255,255,255,0.85)";
            return (
              <Box key={dpd} sx={{
                height: cellH, borderRadius: "3px", background: heatColor(c.count),
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                fontSize: 10, color: textColor, lineHeight: 1.1,
              }}>
                {c.count > 0 ? (
                  <>
                    <Box>
                      <Box component="span" sx={{ fontWeight: 600 }}>{c.count}</Box>
                      <Box component="span" sx={{ fontSize: 9, color: subColor, ml: "3px" }}>
                        {countPct.toFixed(1)}%
                      </Box>
                    </Box>
                    {c.keptAmt > 0 && (
                      <Box sx={{ fontSize: 9, color: subColor }}>
                        KEPT {fmtCurrency(c.keptAmt)} · {keptPct.toFixed(0)}%
                      </Box>
                    )}
                  </>
                ) : ""}
              </Box>
            );
          })}
          {/* Row total */}
          <Box sx={{ ...totalCellSx, height: cellH, flexDirection: "column",
            gap: 0, lineHeight: 1.1 }}>
            <Box>{rowTotals[ri] || ""}</Box>
            {rowKept[ri] > 0 && <Box sx={{ fontSize: 9 }}>KEPT {fmtCurrency(rowKept[ri])}</Box>}
          </Box>
        </Box>
      ))}

      {/* Column totals */}
      <Box sx={{ display: "grid", gridTemplateColumns: colTpl, gap: "2px", mt: "2px" }}>
        <Box sx={{ fontSize: 10, color: BLUE, fontWeight: 600,
          lineHeight: `${totH}px`, textAlign: "right", pr: 1 }}>
          Total
        </Box>
        {colTotals.map((v, di) => (
          <Box key={di} sx={{ ...totalCellSx, height: totH, flexDirection: "column",
            gap: 0, lineHeight: 1.1 }}>
            <Box>{v || ""}</Box>
            {colKept[di] > 0 && <Box sx={{ fontSize: 9 }}>KEPT {fmtCurrency(colKept[di])}</Box>}
          </Box>
        ))}
        <Box sx={{ ...grandTotalCellSx, height: totH, flexDirection: "column",
          gap: 0, lineHeight: 1.1 }}>
          <Box>{grandTotal || ""}</Box>
          {grandKept > 0 && <Box sx={{ fontSize: 9 }}>KEPT {fmtCurrency(grandKept)}</Box>}
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px",
        mt: 1, fontSize: 10, color: "text.secondary", flexWrap: "wrap" }}>
        <span>Low</span>
        {["#E6F1FB","#B5D4F4","#378ADD","#0C447C"].map(c => (
          <Box key={c} sx={{ width: 14, height: 10, background: c, borderRadius: "2px" }} />
        ))}
        <span>High</span>
        <Box sx={{ ml: 1 }}>
          Responses + % of column · KEPT = same-day payments $, shown as a share of
          its column. The promise half is not broken out per cell.
        </Box>
      </Box>
    </Box>
  );
}

// ── Day × hour heatmap (count mode or rate-vs-denominator mode) ────
function DayHourHeatmap({ rows, field = "Responses", denominatorField = null }) {
  const { grid, denomGrid } = useMemo(() => {
    const g = {};
    const d = denominatorField ? {} : null;
    rows.forEach(r => {
      const dow = Number(r.DayOfWeek);
      const h   = Number(r.HourOfDay);
      if (!dow || isNaN(h)) return;
      const { day, hour } = utcToLocal(dow, h);
      const key = `${day}_${hour}`;
      g[key] = (g[key] || 0) + safeNum(r[field]);
      if (d) d[key] = (d[key] || 0) + safeNum(r[denominatorField]);
    });
    return { grid: g, denomGrid: d };
  }, [rows, field, denominatorField]);

  const isRateMode = !!denomGrid;

  const rowTotals = HEATMAP_HOUR_VALUES.map(hour =>
    HEATMAP_DAYS.reduce((sum, _, di) => sum + (grid[`${di + 1}_${hour}`] || 0), 0));
  const colTotals = HEATMAP_DAYS.map((_, di) =>
    HEATMAP_HOUR_VALUES.reduce((sum, hour) => sum + (grid[`${di + 1}_${hour}`] || 0), 0));
  const grandTotal = colTotals.reduce((a, b) => a + b, 0);

  const denomRowTotals = denomGrid
    ? HEATMAP_HOUR_VALUES.map(hour =>
        HEATMAP_DAYS.reduce((sum, _, di) => sum + (denomGrid[`${di + 1}_${hour}`] || 0), 0))
    : null;
  const denomColTotals = denomGrid
    ? HEATMAP_DAYS.map((_, di) =>
        HEATMAP_HOUR_VALUES.reduce((sum, hour) => sum + (denomGrid[`${di + 1}_${hour}`] || 0), 0))
    : null;
  const denomGrandTotal = denomColTotals ? denomColTotals.reduce((a, b) => a + b, 0) : null;

  const cellData = (di, hi) => {
    const hour = HEATMAP_HOUR_VALUES[hi];
    const num  = grid[`${di + 1}_${hour}`] || 0;
    const den  = isRateMode ? (denomGrid[`${di + 1}_${hour}`] || 0) : grandTotal;
    if (den === 0) return null;
    return { num, pct: (num / den) * 100 };
  };

  const heatMax = useMemo(() => {
    let m = 0;
    for (let di = 0; di < HEATMAP_DAYS.length; di++) {
      for (let hi = 0; hi < HEATMAP_HOUR_VALUES.length; hi++) {
        const cd = cellData(di, hi);
        if (!cd) continue;
        if (isRateMode && cd.pct >= 100) continue;
        if (cd.pct > m) m = cd.pct;
      }
    }
    return Math.max(1, m);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid, denomGrid, isRateMode, grandTotal]);

  const heatColor = (pct) => {
    if (pct == null || heatMax === 0) return "#F1EFE8";
    if (isRateMode && pct >= 100) return "#F1EFE8";
    const r = pct / heatMax;
    if (r < 0.2)  return "#E6F1FB";
    if (r < 0.4)  return "#B5D4F4";
    if (r < 0.65) return "#378ADD";
    return "#0C447C";
  };

  const fmtRowTotal = (hi) => {
    if (isRateMode) {
      const den = denomRowTotals[hi];
      return den ? `${(rowTotals[hi] / den * 100).toFixed(1)}%` : "";
    }
    return rowTotals[hi] ? rowTotals[hi].toLocaleString() : "";
  };
  const fmtColTotal = (di) => {
    if (isRateMode) {
      const den = denomColTotals[di];
      return den ? `${(colTotals[di] / den * 100).toFixed(1)}%` : "";
    }
    return colTotals[di] ? colTotals[di].toLocaleString() : "";
  };
  const fmtGrandTotal = () => {
    if (isRateMode) {
      return denomGrandTotal ? `${(grandTotal / denomGrandTotal * 100).toFixed(1)}%` : "";
    }
    return grandTotal ? grandTotal.toLocaleString() : "";
  };

  const colTpl = `42px repeat(7, 1fr) 60px`;
  const cellH = 32;

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: colTpl, gap: "2px", fontSize: 10 }}>
        <Box />
        {HEATMAP_DAYS.map(d => (
          <Box key={d} sx={{ textAlign: "center", color: "text.secondary",
            pb: 0.5, fontWeight: 500 }}>{d}</Box>
        ))}
        <Box sx={{ textAlign: "center", color: BLUE, pb: 0.5, fontWeight: 600 }}>Total</Box>

        {HEATMAP_HOURS.map((h, hi) => (
          <React.Fragment key={h}>
            <Box sx={{ textAlign: "right", pr: 1, color: "text.secondary",
              lineHeight: `${cellH}px`, height: cellH }}>
              {h}
            </Box>
            {HEATMAP_DAYS.map((_, di) => {
              const cd = cellData(di, hi);
              if (!cd) {
                return <Box key={di} sx={{ height: cellH, borderRadius: "2px", background: "#F1EFE8" }} />;
              }
              const isNeutral = isRateMode && cd.pct >= 100;
              const isLight   = isNeutral || cd.pct < heatMax * 0.6;
              const textColor = isLight ? "text.primary"   : "#fff";
              const pctColor  = isLight ? "text.secondary" : "rgba(255,255,255,0.85)";
              return (
                <Box key={di} sx={{
                  height: cellH, borderRadius: "2px", background: heatColor(cd.pct),
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  fontSize: 10, color: textColor, lineHeight: 1.15,
                }}>
                  {cd.num > 0 ? (
                    <>
                      <Box sx={{ fontWeight: 600 }}>{cd.num.toLocaleString()}</Box>
                      <Box sx={{ fontSize: 9, color: pctColor, mt: "1px" }}>
                        {cd.pct.toFixed(1)}%
                      </Box>
                    </>
                  ) : ""}
                </Box>
              );
            })}
            <Box sx={{ ...totalCellSx, height: cellH }}>{fmtRowTotal(hi)}</Box>
          </React.Fragment>
        ))}

        <Box sx={{ textAlign: "right", pr: 1, color: BLUE, fontWeight: 600,
          lineHeight: "22px", height: 22 }}>
          Total
        </Box>
        {HEATMAP_DAYS.map((_, di) => (
          <Box key={di} sx={totalCellSx}>{fmtColTotal(di)}</Box>
        ))}
        <Box sx={grandTotalCellSx}>{fmtGrandTotal()}</Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "4px",
        mt: 1, fontSize: 10, color: "text.secondary" }}>
        <span>Low</span>
        {["#E6F1FB","#B5D4F4","#378ADD","#0C447C"].map(c => (
          <Box key={c} sx={{ width: 14, height: 10, background: c, borderRadius: "2px" }} />
        ))}
        <span>High</span>
      </Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const DpdDayActivity = () => {
  const [startDate, setStartDate] = useState(() => new Date());
  const [endDate,   setEndDate]   = useState(() => new Date());
  const [dpdMin,    setDpdMin]    = useState(1);
  const [dpdMax,    setDpdMax]    = useState(10);
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [searched,  setSearched]  = useState(false);
  const [dpdFilter, setDpdFilter] = useState("All"); // heatmap-only DPD pill

  const load = useCallback(async () => {
    if (Number(dpdMin) > Number(dpdMax)) {
      setError("DPD From must be less than or equal to DPD To.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await fetchDpdDayActivity({
        startDate: formatDateTime(startDate),
        endDate:   formatDateTime(endDate),
        dpdMin:    Number(dpdMin),
        dpdMax:    Number(dpdMax),
      });
      setData(result || []);
      setSearched(true);
      setDpdFilter("All");
    } catch (e) {
      console.error(e);
      setError("Failed to load DPD day activity data.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, dpdMin, dpdMax]);

  const dpdValues = useMemo(() => {
    const s = new Set();
    data.forEach(r => { if (r.DPD !== null && r.DPD !== undefined) s.add(r.DPD); });
    return [...s].sort((a, b) => a - b);
  }, [data]);

  // ── KPIs across the full result set ──
  //
  // `Payments` is Kept and `PTP` is Promises, so PTP the METRIC is the two
  // summed. Kept % then reads against that total, which is what makes the two
  // tiles sit next to each other as whole-then-part.
  const kpis = useMemo(() => {
    const sent      = data.reduce((a,b) => a + safeNum(b.TextSent),         0);
    const responses = data.reduce((a,b) => a + safeNum(b.Responses),        0);
    const respMins  = data.reduce((a,b) => a + safeNum(b.RespMinutesTotal), 0);
    const keptCnt   = data.reduce((a,b) => a + safeNum(b.Payments),         0);
    const keptAmt   = data.reduce((a,b) => a + safeNum(b.PaymentAmount),    0);
    const uncCnt    = data.reduce((a,b) => a + safeNum(b.PTP),              0);
    const uncAmt    = data.reduce((a,b) => a + safeNum(b.PromiseAmount),    0);

    const ptpCnt = keptCnt + uncCnt;
    const ptpAmt = keptAmt + uncAmt;

    return {
      sent, responses, keptCnt, keptAmt, uncCnt, uncAmt, ptpCnt, ptpAmt,
      pct:     sent      ? (responses / sent) * 100 : 0,
      avgMin:  responses ? respMins / responses     : 0,
      keptPct: ptpAmt    ? (keptAmt / ptpAmt) * 100 : 0,
    };
  }, [data]);

  // ── Averages line — one point per DPD ──
  // Left axis: response %; right axis: avg first-response time (minutes).
  const dpdLineData = useMemo(() => {
    const map = {};
    data.forEach(r => {
      const d = r.DPD;
      if (d === null || d === undefined) return;
      if (!map[d]) map[d] = { dpd: d, sent: 0, resp: 0, mins: 0 };
      map[d].sent += safeNum(r.TextSent);
      map[d].resp += safeNum(r.Responses);
      map[d].mins += safeNum(r.RespMinutesTotal);
    });
    return Object.values(map)
      .sort((a, b) => a.dpd - b.dpd)
      .map(m => ({
        dpd:       `DPD ${m.dpd}`,
        respPct:   m.sent ? Number(((m.resp / m.sent) * 100).toFixed(1)) : null,
        avgRespMin: m.resp ? Number((m.mins / m.resp).toFixed(1))        : null,
      }));
  }, [data]);

  // ── Day × hour rows, honoring the DPD pill filter ──
  const heatmapRows = useMemo(() =>
    dpdFilter === "All" ? data : data.filter(r => r.DPD === dpdFilter)
  , [data, dpdFilter]);

  // ── Detail table — date × dpd ──
  const detailRows = useMemo(() => {
    const map = {};
    data.forEach(r => {
      const key = `${String(r.SummaryDate).slice(0,10)}_${r.DPD}`;
      if (!map[key]) map[key] = {
        date: String(r.SummaryDate).slice(0,10), dpd: r.DPD,
        sent: 0, resp: 0, mins: 0, keptCnt: 0, keptAmt: 0, uncCnt: 0, uncAmt: 0,
      };
      map[key].sent    += safeNum(r.TextSent);
      map[key].resp    += safeNum(r.Responses);
      map[key].mins    += safeNum(r.RespMinutesTotal);
      map[key].keptCnt += safeNum(r.Payments);
      map[key].keptAmt += safeNum(r.PaymentAmount);
      map[key].uncCnt  += safeNum(r.PTP);
      map[key].uncAmt  += safeNum(r.PromiseAmount);
    });
    return Object.values(map).sort((a, b) =>
      a.date === b.date ? a.dpd - b.dpd : a.date.localeCompare(b.date));
  }, [data]);

  const displayedRows = useMemo(() => detailRows.slice(0, 300), [detailRows]);

  const visibleTotals = useMemo(() => ({
    sent:    displayedRows.reduce((a,b) => a + b.sent,    0),
    resp:    displayedRows.reduce((a,b) => a + b.resp,    0),
    mins:    displayedRows.reduce((a,b) => a + b.mins,    0),
    keptCnt: displayedRows.reduce((a,b) => a + b.keptCnt, 0),
    keptAmt: displayedRows.reduce((a,b) => a + b.keptAmt, 0),
    uncCnt:  displayedRows.reduce((a,b) => a + b.uncCnt,  0),
    uncAmt:  displayedRows.reduce((a,b) => a + b.uncAmt,  0),
  }), [displayedRows]);

  const axisStyle = { fontSize: 10, fill: "#888" };

  // PTP $ is a derived column — Kept $ + Promises $ — so the table carries
  // the same total the tile does rather than leaving the reader to add two
  // columns in their head.
  const COLS = "90px 55px 85px 90px 100px 100px 95px 85px 95px";
  const HEADERS = [
    "Date", "DPD", "Text Sent", "Responses", "Response %", "Avg Resp",
    "PTP $", "Kept #", "Kept $",
  ];

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Filters ─────────────────────────────────────────── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d >= startDate) setEndDate(d); }}
          marginRight={0}
        />
        <TextField
          label="DPD From"
          type="number"
          size="small"
          value={dpdMin}
          onChange={e => setDpdMin(e.target.value)}
          inputProps={{ min: 0 }}
          sx={{ width: 100 }}
        />
        <TextField
          label="DPD To"
          type="number"
          size="small"
          value={dpdMax}
          onChange={e => setDpdMax(e.target.value)}
          inputProps={{ min: 0 }}
          sx={{ width: 100 }}
        />
        <Box
          onClick={load}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && searched && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── KPI TILES ─────────────────────────────────────── */}
          {/* PTP leads the money pair and Kept follows it: whole, then the part
              of it that arrived. Kept % is against PTP $, which is the ratio
              that ordering promises. */}
          <SectionTitle>Summary — DPD {dpdMin}–{dpdMax}</SectionTitle>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <KpiTile label="Text Sent"
              value={kpis.sent.toLocaleString()}
              color={C_SENT} />
            <KpiTile label="Responses"
              value={kpis.responses.toLocaleString()}
              sub={`${kpis.pct.toFixed(1)}% of sent`}
              color={C_RPC} />
            <KpiTile label="Avg Response Time"
              value={fmtDuration(kpis.avgMin)}
              sub="First reply after send"
              color={AMBER} />
            <KpiTile label="PTP"
              value={fmtCurrency(kpis.ptpAmt)}
              sub={`${kpis.ptpCnt.toLocaleString()} — Kept + Promises`}
              color={C_PTP} />
            <KpiTile label="Kept"
              value={fmtCurrency(kpis.keptAmt)}
              sub={`${kpis.keptCnt.toLocaleString()} · ${kpis.keptPct.toFixed(1)}% of PTP`}
              color={C_KEPT} />
          </Box>

          <Typography sx={{
            fontSize: 11, color: "text.disabled", mb: 2, lineHeight: 1.5,
          }}>
            <b>PTP</b> is Kept + Promises; <b>Kept %</b> is Kept $ over PTP $. Promises
            are future payments scheduled through PNM — they feed PTP and are not shown
            on their own. The backend column named <code>PTP</code> holds that promise
            half; on this page the word PTP always means the total.
          </Typography>

          {/* ── AVERAGES LINE — by DPD ────────────────────────── */}
          <SectionTitle>Averages by DPD</SectionTitle>
          <ChartCard height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dpdLineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="dpd" tick={axisStyle} />
                <YAxis
                  yAxisId="pct"
                  tick={axisStyle}
                  tickFormatter={v => `${v}%`}
                  domain={[0, "auto"]}
                />
                <YAxis
                  yAxisId="min"
                  orientation="right"
                  tick={axisStyle}
                  tickFormatter={v => fmtDuration(v)}
                  domain={[0, "auto"]}
                />
                <Tooltip
                  contentStyle={{ fontSize: 12 }}
                  formatter={(v, n) =>
                    n === "Avg Response Time"
                      ? [fmtDuration(v), n]
                      : [`${v}%`, n]}
                />
                <Legend iconSize={10} iconType="plainline" wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="pct" type="monotone" dataKey="respPct"
                  name="Response %" stroke={C_RPC}
                  strokeWidth={2} dot={{ r: 2.5 }} connectNulls={false} />
                <Line yAxisId="min" type="monotone" dataKey="avgRespMin"
                  name="Avg Response Time" stroke={AMBER}
                  strokeWidth={2} dot={{ r: 2.5 }} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── RESPONSE TIME × DPD MATRIX ────────────────────── */}
          <SectionTitle>Response time by DPD</SectionTitle>
          <Box sx={{ border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, p: "12px 14px", mb: 1.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.5 }}>
              Response time bucket × DPD day
            </Typography>
            <DpdDayTimingHeatmap rows={data} dpdValues={dpdValues} />
          </Box>

          {/* ── DAY × HOUR HEATMAPS — filterable by DPD ───────── */}
          <SectionTitle>Day × hour heatmaps</SectionTitle>
          <Box sx={{ border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, p: "12px 14px", mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 11, color: "text.secondary",
                textTransform: "uppercase", letterSpacing: "0.05em", mr: 0.5 }}>
                Filter by DPD
              </Typography>
              {["All", ...dpdValues].map(d => {
                const active = dpdFilter === d;
                return (
                  <Box key={d} onClick={() => setDpdFilter(d)} sx={{
                    px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                    cursor: "pointer", userSelect: "none", border: "0.5px solid",
                    borderColor: active ? BLUE : "divider",
                    background:  active ? BLUE : "transparent",
                    color:       active ? "#fff" : "text.secondary",
                    fontWeight:  active ? 500 : 400,
                    transition: "all .15s",
                    "&:hover": { borderColor: BLUE },
                  }}>{d === "All" ? "All" : `DPD ${d}`}</Box>
                );
              })}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary",
                  textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                  Sends — day × hour (count + % of total)
                </Typography>
                <DayHourHeatmap rows={heatmapRows} field="TextSent" />
              </Box>

              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary",
                  textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                  Responses — day × hour (count + Response % vs sent)
                </Typography>
                <DayHourHeatmap
                  rows={heatmapRows}
                  field="Responses"
                  denominatorField="TextSent"
                />
              </Box>
            </Box>
          </Box>

          {/* ── DETAIL TABLE — date × DPD ─────────────────────── */}
          <SectionTitle>Detail</SectionTitle>
          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "auto",
          }}>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1,
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {HEADERS.map((h, i) => (
                <Box key={h} sx={{ textAlign: i === 0 ? "left" : "right" }}>{h}</Box>
              ))}
            </Box>

            {displayedRows.map((row, i) => (
              <Box key={`${row.date}_${row.dpd}`} sx={{
                display: "grid",
                gridTemplateColumns: COLS,
                px: 2, py: "6px",
                fontSize: 12,
                borderBottom: "0.5px solid", borderColor: "divider",
                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                "&:hover": { background: "rgba(55,138,221,0.06)" },
              }}>
                <Box>{fmtDate(row.date)}</Box>
                <Box sx={{ textAlign: "right" }}>{row.dpd ?? "—"}</Box>
                <Box sx={{ textAlign: "right", color: C_SENT, fontWeight: 500 }}>
                  {row.sent.toLocaleString()}
                </Box>
                <Box sx={{ textAlign: "right", color: C_RPC, fontWeight: 500 }}>
                  {row.resp.toLocaleString()}
                </Box>
                <Box sx={{ textAlign: "right", color: C_RPC }}>
                  {row.sent ? `${((row.resp / row.sent) * 100).toFixed(1)}%` : "—"}
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  {row.resp ? fmtDuration(row.mins / row.resp) : "—"}
                </Box>
                <Box sx={{ textAlign: "right", color: C_PTP, fontWeight: 500 }}>
                  {row.keptAmt + row.uncAmt
                    ? fmtCurrency(row.keptAmt + row.uncAmt)
                    : "—"}
                </Box>
                <Box sx={{ textAlign: "right", color: C_KEPT }}>{row.keptCnt.toLocaleString()}</Box>
                <Box sx={{ textAlign: "right", color: C_KEPT, fontWeight: 500 }}>
                  {row.keptAmt ? fmtCurrency(row.keptAmt) : "—"}
                </Box>
              </Box>
            ))}

            {/* Totals row */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              px: 2, py: "8px",
              fontSize: 12, fontWeight: 600,
              borderTop: "1px solid", borderColor: "divider",
              background: "#f0f3f7",
            }}>
              <Box>Total</Box>
              <Box sx={{ textAlign: "right" }}>—</Box>
              <Box sx={{ textAlign: "right", color: C_SENT }}>{visibleTotals.sent.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right", color: C_RPC  }}>{visibleTotals.resp.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right", color: C_RPC  }}>
                {visibleTotals.sent
                  ? `${((visibleTotals.resp / visibleTotals.sent) * 100).toFixed(1)}%`
                  : "—"}
              </Box>
              <Box sx={{ textAlign: "right" }}>
                {visibleTotals.resp ? fmtDuration(visibleTotals.mins / visibleTotals.resp) : "—"}
              </Box>
              <Box sx={{ textAlign: "right", color: C_PTP }}>
                {fmtCurrency(visibleTotals.keptAmt + visibleTotals.uncAmt)}
              </Box>
              <Box sx={{ textAlign: "right", color: C_KEPT }}>{visibleTotals.keptCnt.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right", color: C_KEPT }}>{fmtCurrency(visibleTotals.keptAmt)}</Box>
            </Box>
          </Box>

          {detailRows.length > displayedRows.length && (
            <Typography sx={{
              fontSize: 11, color: "text.secondary",
              mt: 1, textAlign: "right",
            }}>
              Showing {displayedRows.length.toLocaleString()} of {detailRows.length.toLocaleString()} rows.
              KPI cards, charts, and heatmaps above reflect the full range.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default DpdDayActivity;